import { ArrowRight, LogOut, UserCircle } from "lucide-react";
import { useCallback, useId, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  isNavigationItemActive,
  type NavigationGroup,
  type NavigationItem,
  navigationGroups,
  resolveCurrentNavigationItem,
  shellQuickLinks,
} from "@/app/navigation";
import { InstallAppEntry } from "@/app/InstallAppEntry";
import { useNavigationPrefetch } from "@/app/route-prefetch";
import { InfoTooltip } from "@/components/shared/utility/InfoTooltip";
import { Button } from "@/components/ui/button";
import { cardVariants } from "@/components/ui/card-variants";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/components/ui/utils";
import { useAuth } from "@/features/auth";
import { useNotifications } from "@/features/notifications/notifications-context";
import { formatBadgeCount } from "@/features/notifications/notifications-format";

const brandLinkClassName = cn(cardVariants({ variant: "raised" }), "flex items-center gap-3 p-4");

/** Sidebar unread badge for the Notifications nav item. */
function NotificationsNavBadge() {
  const { unreadCount } = useNotifications();

  if (unreadCount === 0) {
    return null;
  }

  return (
    <SidebarMenuBadge className="bg-primary font-semibold text-primary-foreground">
      {formatBadgeCount(unreadCount)}
      <span className="sr-only"> unread</span>
    </SidebarMenuBadge>
  );
}

function NavigationMenuItem({
  item,
  pathname,
  currentPath,
  prefetchOnFocus,
  schedulePrefetch,
  cancelPrefetch,
  onSelect,
}: {
  cancelPrefetch: () => void;
  currentPath: string | undefined;
  item: NavigationItem;
  onSelect: () => void;
  pathname: string;
  prefetchOnFocus: (pathname: string) => void;
  schedulePrefetch: (pathname: string) => void;
}) {
  const isActive = isNavigationItemActive(pathname, item.path);

  return (
    <SidebarMenuItem key={item.path}>
      <SidebarMenuButton
        asChild
        className="surface-sheen rounded-2xl border border-transparent p-3 transition-[background-color,border-color,box-shadow,color] hover:border-sidebar-border/55 hover:bg-sidebar-accent/62 hover:shadow-sm data-[active=true]:border-sidebar-border/75 data-[active=true]:bg-sidebar-accent/90 data-[active=true]:shadow-md"
        isActive={isActive}
        size="lg"
        tooltip={`${item.label}: ${item.description}`}
      >
        <Link
          aria-current={item.path === currentPath ? "page" : undefined}
          onClick={onSelect}
          onFocus={() => prefetchOnFocus(item.path)}
          onPointerEnter={() => schedulePrefetch(item.path)}
          onPointerLeave={cancelPrefetch}
          to={item.path}
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-sidebar-accent/55 text-sidebar-primary transition-[background-color,color,box-shadow] data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:shadow-sm">
            <item.icon className="size-4" />
          </span>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate font-medium text-[13px]">{item.label}</span>
            <span className="sr-only">: {item.description}</span>
          </span>
        </Link>
      </SidebarMenuButton>
      {item.path === "/notifications" && <NotificationsNavBadge />}
    </SidebarMenuItem>
  );
}

function NavigationGroupSection({
  group,
  pathname,
  currentPath,
  prefetchOnFocus,
  schedulePrefetch,
  cancelPrefetch,
  onSelect,
  showDetails,
}: {
  cancelPrefetch: () => void;
  currentPath: string | undefined;
  group: NavigationGroup;
  onSelect: () => void;
  pathname: string;
  prefetchOnFocus: (pathname: string) => void;
  schedulePrefetch: (pathname: string) => void;
  showDetails: boolean;
}) {
  return (
    <SidebarGroup className="px-2" key={group.label}>
      <div className="mb-2 flex items-start justify-between gap-3 px-2">
        <div className="flex min-w-0 items-center gap-2">
          <SidebarGroupLabel className="px-0 pb-0 text-sidebar-foreground">
            {group.label}
          </SidebarGroupLabel>
          {showDetails ? (
            <InfoTooltip className="text-sidebar-foreground/50 hover:text-sidebar-foreground/80">
              {group.description}
            </InfoTooltip>
          ) : null}
        </div>
        {showDetails ? (
          <SidebarMenuBadge className="static rounded-md border border-sidebar-border/70 bg-sidebar-accent/40 px-2 py-0.5 text-[10px] text-numeric text-sidebar-foreground">
            {group.items.length}
          </SidebarMenuBadge>
        ) : null}
      </div>
      <SidebarGroupContent>
        <SidebarMenu className="gap-1">
          {group.items.map((item) => (
            <NavigationMenuItem
              currentPath={currentPath}
              item={item}
              key={item.path}
              pathname={pathname}
              prefetchOnFocus={prefetchOnFocus}
              schedulePrefetch={schedulePrefetch}
              cancelPrefetch={cancelPrefetch}
              onSelect={onSelect}
            />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function SidebarProfileCard() {
  const { profile, signOut } = useAuth();
  const profileLabel = profile?.full_name || profile?.email || "Operator";
  const profileRole = profile?.role || "staff";
  const headingId = useId();
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    // The card is a focus target of last resort: the install entry removes
    // itself when used, and focus moves here instead of falling to <body>.
    <div
      aria-labelledby={headingId}
      className="surface-sheen rounded-xl border border-sidebar-border/60 bg-sidebar-accent/38 p-3 outline-hidden ring-sidebar-ring focus-visible:ring-2 group-data-[collapsible=icon]:hidden"
      ref={cardRef}
      role="group"
      tabIndex={-1}
    >
      <p
        className="mb-2 font-medium text-[10px] text-sidebar-foreground/70 uppercase tracking-[0.18em]"
        id={headingId}
      >
        Profile
      </p>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <UserCircle aria-hidden="true" className="size-8 shrink-0 text-sidebar-foreground/70" />
          <div className="min-w-0">
            <p className="truncate font-medium text-sidebar-foreground text-sm">{profileLabel}</p>
            <p className="truncate text-[11px] text-sidebar-foreground/60">{profileRole}</p>
          </div>
        </div>
        <Button
          aria-label="Sign out"
          className="shrink-0 rounded-lg text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring"
          onClick={() => {
            Promise.resolve(signOut()).catch(() => undefined);
          }}
          size="mobileTouchIcon"
          type="button"
          variant="ghost"
        >
          <LogOut aria-hidden="true" className="size-4" />
        </Button>
      </div>
      <InstallAppEntry className="mt-3" onSettled={() => cardRef.current?.focus()} />
    </div>
  );
}

function QuickLinksCard({
  cancelPrefetch,
  onSelect,
  prefetchOnFocus,
  schedulePrefetch,
}: {
  cancelPrefetch: () => void;
  onSelect: () => void;
  prefetchOnFocus: (pathname: string) => void;
  schedulePrefetch: (pathname: string) => void;
}) {
  return (
    <div className="surface-sheen rounded-xl border border-sidebar-border/60 bg-sidebar-accent/38 p-3.5 group-data-[collapsible=icon]:hidden">
      <p className="font-medium text-[10px] text-sidebar-foreground/70 uppercase tracking-[0.18em]">
        Priority workflows
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {shellQuickLinks.map((link) => (
          <Button
            asChild
            className="surface-sheen justify-between rounded-xl border border-transparent font-normal text-sidebar-foreground/85 hover:border-sidebar-border/55 hover:bg-sidebar-accent/88 hover:text-sidebar-accent-foreground"
            key={link.path}
            size="mobileTouch"
            variant="ghost"
          >
            <Link
              onClick={onSelect}
              onFocus={() => prefetchOnFocus(link.path)}
              onPointerEnter={() => schedulePrefetch(link.path)}
              onPointerLeave={cancelPrefetch}
              to={link.path}
            >
              <span>{link.label}</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Renders the authenticated app sidebar with navigation, profile actions, and quick links. */
export function Layout() {
  const location = useLocation();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const { cancelPrefetch, prefetchOnFocus, schedulePrefetch } = useNavigationPrefetch();
  const showDetails = isMobile || state === "expanded";
  const currentPath = resolveCurrentNavigationItem(location.pathname)?.path;

  /*
   * The mobile sidebar is a modal Sheet whose own close button is suppressed,
   * so routing without closing leaves the drawer covering the destination with
   * no visible way out. The desktop sidebar is persistent chrome that must keep
   * its expanded/collapsed state across navigations, hence the `isMobile` gate.
   */
  const selectDestination = useCallback(() => {
    cancelPrefetch();
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [cancelPrefetch, isMobile, setOpenMobile]);

  return (
    <Sidebar
      className="border-sidebar-border/60 border-r bg-sidebar/95 shadow-md backdrop-blur-xl"
      collapsible="icon"
    >
      <SidebarHeader className="p-4">
        <Link className={brandLinkClassName} onClick={selectDestination} to="/">
          <div className="flex size-10 items-center justify-center rounded-2xl bg-sidebar-primary text-sidebar-primary-foreground shadow-sm">
            <span className="font-medium text-sm">X</span>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0">
              <p className="font-medium text-[11px] text-sidebar-foreground/60 uppercase tracking-[0.18em]">
                Operations Shell
              </p>
              <h2 className="truncate font-medium text-[15px]">XanBZS ERP</h2>
            </div>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="gap-5 px-2 pb-4">
        {navigationGroups.map((group) => (
          <NavigationGroupSection
            currentPath={currentPath}
            group={group}
            key={group.label}
            pathname={location.pathname}
            prefetchOnFocus={prefetchOnFocus}
            schedulePrefetch={schedulePrefetch}
            cancelPrefetch={cancelPrefetch}
            onSelect={selectDestination}
            showDetails={showDetails}
          />
        ))}
      </SidebarContent>

      <SidebarFooter className="gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {showDetails ? (
          <>
            <SidebarProfileCard />
            <QuickLinksCard
              cancelPrefetch={cancelPrefetch}
              onSelect={selectDestination}
              prefetchOnFocus={prefetchOnFocus}
              schedulePrefetch={schedulePrefetch}
            />
          </>
        ) : null}
      </SidebarFooter>
    </Sidebar>
  );
}
